import React, { useCallback } from 'react';
import { MapPin, Users, Building2, Scale, Shield, Map, X, Layers, Download } from 'lucide-react';
import type { FUNAITerritoryProperties } from '../types';
import './TerritoryInfo.css';

// Summary data for multiple territories
export interface TerritorySummary {
  count: number;
  totalAreaHa: number;
  territories: Array<{
    code: number;
    name: string;
    areaHa: number;
    phase?: string;
    state?: string;
  }>;
  uniqueStates: string[];
  uniquePhases: string[];
  totalBoundaryPoints: number;
}

interface TerritoryInfoProps {
  // Single territory mode
  metadata?: FUNAITerritoryProperties;
  boundaryPoints?: number;
  boundary?: [number, number][][];
  // Multi-territory summary mode
  summary?: TerritorySummary;
  // Common
  onClose?: () => void;
}

// Translate legal phase to English
function translatePhase(phase: string | undefined): string {
  if (!phase) return 'N/A';
  const translations: Record<string, string> = {
    'Regularizada': 'Regularized',
    'Homologada': 'Homologated',
    'Declarada': 'Declared',
    'Delimitada': 'Delimited',
    'Em Estudo': 'Under Study',
    'Encaminhada RI': 'Forwarded RI',
    'Portaria de Restrição': 'Restriction Order'
  };
  return translations[phase] || phase;
}

// Translate modality
function translateModality(modality: string | undefined): string {
  if (!modality) return 'N/A';
  const translations: Record<string, string> = {
    'Tradicionalmente ocupada': 'Traditionally Occupied',
    'Reserva Indígena': 'Indigenous Reserve',
    'Dominial Indígena': 'Indigenous Domain',
    'Interditada': 'Interdicted'
  };
  return translations[modality] || modality;
}

// Format area with thousands separator
function formatArea(area: number | undefined): string {
  if (!area) return 'N/A';
  return area.toLocaleString('en-US', { maximumFractionDigits: 2 }) + ' ha';
}

// Single territory detail view
const SingleTerritoryView: React.FC<{
  metadata: FUNAITerritoryProperties;
  boundaryPoints: number;
}> = ({ metadata, boundaryPoints }) => (
  <>
    <div className="territory-info-grid">
      <div className="info-item">
        <Users size={16} />
        <div>
          <span className="info-label">Ethnicity</span>
          <span className="info-value">{metadata.etnia_nome || 'N/A'}</span>
        </div>
      </div>

      <div className="info-item">
        <Building2 size={16} />
        <div>
          <span className="info-label">Municipality</span>
          <span className="info-value">{metadata.municipio_nome || 'N/A'}</span>
        </div>
      </div>

      <div className="info-item">
        <Map size={16} />
        <div>
          <span className="info-label">State</span>
          <span className="info-value">{metadata.uf_sigla || 'N/A'}</span>
        </div>
      </div>

      <div className="info-item">
        <Scale size={16} />
        <div>
          <span className="info-label">Area</span>
          <span className="info-value highlight">{formatArea(metadata.superficie_perimetro_ha)}</span>
        </div>
      </div>

      <div className="info-item">
        <Shield size={16} />
        <div>
          <span className="info-label">Legal Phase</span>
          <span className="info-value">{translatePhase(metadata.fase_ti)}</span>
        </div>
      </div>

      <div className="info-item">
        <MapPin size={16} />
        <div>
          <span className="info-label">Modality</span>
          <span className="info-value">{translateModality(metadata.modalidade_ti)}</span>
        </div>
      </div>
    </div>

    <div className="territory-info-footer">
      <div className="footer-item">
        <span className="footer-label">Regional Coord.</span>
        <span className="footer-value">{metadata.cr || 'N/A'}</span>
      </div>
      <div className="footer-item">
        <span className="footer-label">Border Zone</span>
        <span className="footer-value">{metadata.faixa_fronteira === 'Sim' ? 'Yes' : 'No'}</span>
      </div>
      <div className="footer-item">
        <span className="footer-label">Boundary Points</span>
        <span className="footer-value">{boundaryPoints.toLocaleString()}</span>
      </div>
    </div>
  </>
);

// Multi-territory summary view
const MultiTerritorySummaryView: React.FC<{ summary: TerritorySummary }> = ({ summary }) => (
  <>
    <div className="territory-summary-stats">
      <div className="summary-stat primary">
        <Scale size={24} />
        <div>
          <span className="stat-value">{formatArea(summary.totalAreaHa)}</span>
          <span className="stat-label">Total Area</span>
        </div>
      </div>

      <div className="summary-stat-row">
        <div className="summary-stat">
          <Layers size={18} />
          <div>
            <span className="stat-value">{summary.count}</span>
            <span className="stat-label">Territories</span>
          </div>
        </div>

        <div className="summary-stat">
          <Map size={18} />
          <div>
            <span className="stat-value">{summary.uniqueStates.length}</span>
            <span className="stat-label">States</span>
          </div>
        </div>

        <div className="summary-stat">
          <MapPin size={18} />
          <div>
            <span className="stat-value">{summary.totalBoundaryPoints.toLocaleString()}</span>
            <span className="stat-label">Points</span>
          </div>
        </div>
      </div>
    </div>

    <div className="territory-summary-phases">
      <span className="phases-label">Legal Phases:</span>
      <div className="phases-list">
        {summary.uniquePhases.map(phase => (
          <span key={phase} className={`phase-badge phase-${phase.toLowerCase().replace(/\s+/g, '-')}`}>
            {translatePhase(phase)}
          </span>
        ))}
      </div>
    </div>

    <div className="territory-summary-list">
      <div className="list-header">Selected Territories</div>
      <div className="list-scroll">
        {summary.territories.map((t, index) => (
          <div key={t.code} className="territory-row">
            <span className="row-index">{index + 1}</span>
            <span className="row-code">{t.code}</span>
            <span className="row-name">{t.name}</span>
            <span className="row-area">{formatArea(t.areaHa)}</span>
          </div>
        ))}
      </div>
    </div>
  </>
);

// Generate KML from territory boundary coordinates (supports multi-ring)
function generateTerritoryKML(
  name: string,
  boundary: [number, number][][],
  metadata?: FUNAITerritoryProperties
): string {
  const makePolygonXml = (ring: [number, number][]) => {
    const coords = ring.map(c => `${c[1]},${c[0]},0`).join(' ');
    const closed = coords + ` ${ring[0][1]},${ring[0][0]},0`;
    return `      <Polygon>
        <tessellate>1</tessellate>
        <outerBoundaryIs>
          <LinearRing>
            <coordinates>${closed}</coordinates>
          </LinearRing>
        </outerBoundaryIs>
      </Polygon>`;
  };

  const geometryXml = boundary.length === 1
    ? makePolygonXml(boundary[0])
    : `      <MultiGeometry>\n${boundary.map(makePolygonXml).join('\n')}\n      </MultiGeometry>`;

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>Terra Indigena: ${name}</name>
    <description>FUNAI Territory${metadata ? ` - Code ${metadata.terrai_codigo}` : ''}</description>
    <Placemark>
      <name>${name}</name>
      <description><![CDATA[
        <p><strong>Territory:</strong> ${name}</p>
        ${metadata ? `<p><strong>Code:</strong> ${metadata.terrai_codigo}</p>
        <p><strong>Ethnicity:</strong> ${metadata.etnia_nome || 'N/A'}</p>
        <p><strong>Area:</strong> ${formatArea(metadata.superficie_perimetro_ha)}</p>
        <p><strong>Municipality:</strong> ${metadata.municipio_nome || 'N/A'}</p>
        <p><strong>State:</strong> ${metadata.uf_sigla || 'N/A'}</p>
        <p><strong>Phase:</strong> ${metadata.fase_ti || 'N/A'}</p>
        <p><strong>Regional Coordination:</strong> ${metadata.cr || 'N/A'}</p>` : ''}
        <p><strong>Source:</strong> FUNAI GeoServer</p>
      ]]></description>
      <Style>
        <LineStyle>
          <color>ff0055ff</color>
          <width>2</width>
        </LineStyle>
        <PolyStyle>
          <color>400055ff</color>
        </PolyStyle>
      </Style>
${geometryXml}
    </Placemark>
  </Document>
</kml>`;
}

export const TerritoryInfo: React.FC<TerritoryInfoProps> = ({
  metadata,
  boundaryPoints = 0,
  boundary,
  summary,
  onClose
}) => {
  const isMultiMode = !!summary && summary.count > 1;

  const handleDownloadKML = useCallback(() => {
    if (!boundary || boundary.length === 0) return;
    const name = metadata?.terrai_nome || 'territory';
    const kmlContent = generateTerritoryKML(name, boundary, metadata);
    const blob = new Blob([kmlContent], { type: 'application/vnd.google-earth.kml+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const safeName = name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    link.download = `${safeName}_boundary.kml`;
    link.click();
    URL.revokeObjectURL(url);
  }, [boundary, metadata]);

  return (
    <div className={`territory-info ${isMultiMode ? 'multi-mode' : ''}`}>
      <div className="territory-info-header">
        <div className="territory-info-title">
          <MapPin size={18} className="title-icon" />
          <div>
            {isMultiMode ? (
              <>
                <h3>{summary.count} Territories Selected</h3>
                <span className="territory-code">Combined View</span>
              </>
            ) : metadata ? (
              <>
                <h3>{metadata.terrai_nome}</h3>
                <span className="territory-code">Code: {metadata.terrai_codigo}</span>
              </>
            ) : (
              <h3>No Territory Selected</h3>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {boundary && boundary.length > 0 && !isMultiMode && (
            <button
              className="close-btn"
              onClick={handleDownloadKML}
              title="Download boundary as KML"
              style={{ color: '#c9a961' }}
            >
              <Download size={18} />
            </button>
          )}
          {onClose && (
            <button className="close-btn" onClick={onClose} title="Close">
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {isMultiMode ? (
        <MultiTerritorySummaryView summary={summary} />
      ) : metadata ? (
        <SingleTerritoryView metadata={metadata} boundaryPoints={boundaryPoints} />
      ) : null}

      <div className="territory-info-source">
        Source: FUNAI GeoServer
      </div>
    </div>
  );
};
